import { Injectable, effect, inject, untracked } from '@angular/core';

import { CompanyService } from '../company/company.service';
import { SyncService } from '../offline/sync.service';
import { KsefCertificateService } from './ksef-certificate.service';
import { InvoiceNumberPoolService } from './invoice-number-pool.service';

/**
 * Spina certyfikat KSeF typu Offline z życiem aplikacji kasowej.
 *
 * Same operacje na certyfikacie żyją w `KsefCertificateService` — tutaj jest wyłącznie
 * decyzja, *kiedy* je wywołać:
 *
 *  - po wybraniu firmy (czyli po zalogowaniu, które zawsze odbywa się online)
 *    wczytujemy certyfikat z urządzenia i, gdy go nie ma, pobieramy z systemu;
 *  - po odzyskaniu łączności odsyłamy certyfikat wgrany ręcznie na kasie, żeby
 *    kolejne stanowiska tej samej firmy dostały go automatycznie;
 *  - przy każdej łączności uzupełniamy pulę numerów faktur, bo bez niej sprzedaż
 *    fakturowa offline nie ma czym nadać ostatecznego numeru.
 *
 * Wszystko po cichu: kasa ma sprzedawać także wtedy, gdy KSeF albo sieć nie odpowiada.
 * Nieudane pobranie oznacza brak trybu awaryjnego, a nie zablokowaną sprzedaż.
 */
@Injectable({ providedIn: 'root' })
export class KsefOfflineBootstrapService {
  private readonly certificates = inject(KsefCertificateService);
  private readonly numbers = inject(InvoiceNumberPoolService);
  private readonly company = inject(CompanyService);
  private readonly sync = inject(SyncService);

  /** Firma, dla której certyfikat już obsłużyliśmy — chroni przed powtórkami. */
  private provisionedFor: string | null = null;

  start(): void {
    effect(() => {
      const companyId = this.company.selectedCompanyId();
      const online = this.sync.online();

      untracked(() => void this.reconcile(companyId, online));
    });
  }

  private async reconcile(companyId: string | null, online: boolean): Promise<void> {
    if (!companyId) {
      this.provisionedFor = null;
      return;
    }

    // Certyfikat i pulę numerów wczytujemy z urządzenia zawsze — także bez sieci,
    // bo to ich jedyny sens: kasa ma wystawić dokument przy zerwanej łączności.
    if (this.provisionedFor !== companyId) {
      await this.safely(() => this.certificates.load());
      this.numbers.load(companyId);
    }

    if (!online) {
      return;
    }

    if (this.provisionedFor !== companyId) {
      this.provisionedFor = companyId;
      if (!this.certificates.certificate()) {
        await this.safely(() => this.certificates.provision(companyId));
      }
    }

    if (this.certificates.pendingRegistration()) {
      await this.safely(() => this.certificates.registerPending(companyId));
    }

    // Pulę uzupełniamy dopiero gdy się kurczy — każde pobranie rezerwuje numery
    // trwale, a numer zarezerwowany i niezużyty zostaje luką w numeracji.
    if (this.numbers.needsRefill()) {
      await this.safely(() => this.numbers.refill(this.numberPattern()));
    }
  }

  /**
   * Wzorzec numeracji firmy. Docelowo pochodzi z ustawień firmy pobranych przy
   * logowaniu; dopóki kasa ich nie czyta, serwer użyje własnych wartości domyślnych.
   */
  private numberPattern(): Record<string, never> {
    return {};
  }

  /**
   * Kasa musi sprzedawać niezależnie od tego, czy KSeF odpowiada — błąd zostaje
   * w konsoli, a sprzedaż idzie dalej bez trybu awaryjnego.
   */
  private async safely(action: () => Promise<unknown>): Promise<void> {
    try {
      await action();
    } catch (e) {
      console.warn('KSeF offline: ', e);
    }
  }
}
